import { Box, Icon, Text, Link, Tooltip } from '@nimbus-ds/components';
import {
  DiscountCircleIcon,
  ForbiddenIcon,
  RepeatIcon,
  InfoCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@nimbus-ds/icons';
import { DataList } from '@nimbus-ds/patterns';
import { skipToken } from '@reduxjs/toolkit/query';
import type { SubscriptionItemResponseDto } from '@tiendanube/common';
import { useGetOrderProductsQuery } from 'domains/Customers/Customers/CustomerSubscriptions/subscriptionsApi';
import useTranslationCustomers from 'domains/Customers/hooks/useTranslationCustomers/useTranslationCustomers';
import CustomerSubscriptionsProductsCard from './CustomerSubscriptionsProductsCard';

interface CustomerSubscriptionRowProps {
  sub: SubscriptionItemResponseDto;
  expanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  setSelectedSubscriptionId: (id: string) => void;
  openCancelSubscriptionModal: () => void;
}

function CustomerSubscriptionRow({
  sub,
  expanded,
  onExpand,
  onCollapse,
  setSelectedSubscriptionId,
  openCancelSubscriptionModal,
}: Readonly<CustomerSubscriptionRowProps>) {
  const t = useTranslationCustomers('customerDetails.customerSubscriptions');
  const orderId = sub.initialOrderId.toString();
  const {
    data: products = [],
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
    refetch: fetchProducts,
  } = useGetOrderProductsQuery(expanded ? { orderId } : skipToken, {
    refetchOnMountOrArgChange: false,
  });

  const quantity = sub.products.reduce(
    (sum, product) => sum + product.quantity,
    0,
  );

  return (
    <DataList.Row key={sub.id} gap="1" padding="none">
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap="1" marginTop="2">
          <Text fontWeight="bold" as="span">
            {sub.id}
          </Text>
          <Tooltip content={t('tooltip')} position="bottom">
            <Icon color="primary-interactive" source={<InfoCircleIcon />} />
          </Tooltip>
        </Box>
      </Box>
      <Box>
        <Link
          appearance="primary"
          textDecoration="none"
          onClick={expanded ? onCollapse : onExpand}
        >
          {t('total', { count: quantity })}
          <Icon
            color="primary-interactive"
            source={expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          />
        </Link>
      </Box>

      <Box display="flex" alignItems="center" gap="1">
        <Box display="flex" paddingRight="2" gap="1">
          <Icon source={<RepeatIcon />} />
          <Text fontSize="caption">
            {t('shipmentEveryNDays', {
              days: sub.subscriptionOption.frequencyParam,
            })}
          </Text>
        </Box>
        <Box display="flex" gap="1">
          <Icon source={<DiscountCircleIcon />} />
          <Text fontSize="caption">
            {sub.subscriptionOption.discountPercentage
              ? `${t('discount', {
                  associatedDiscount: sub.subscriptionOption.discountPercentage,
                })}`
              : t('withoutDiscount')}
          </Text>
        </Box>
      </Box>

      <CustomerSubscriptionsProductsCard
        show={expanded}
        products={products}
        isLoading={isLoadingProducts}
        isError={isErrorProducts}
        fetchProducts={fetchProducts}
      />

      <Box
        display="flex"
        justifyContent="flex-start"
        marginTop="3"
        marginBottom="3"
      >
        <Box>
          <Link
            appearance="primary"
            textDecoration="none"
            onClick={() => {
              setSelectedSubscriptionId(sub.id);
              openCancelSubscriptionModal();
            }}
          >
            <Icon color="primary-interactive" source={<ForbiddenIcon />} />
            {t('cancelSubscription')}
          </Link>
        </Box>
      </Box>
    </DataList.Row>
  );
}

export default CustomerSubscriptionRow;
